import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import Topbar from "../Topbar";
import PersonPanel from "./PersonPanel";
import { joinWord, monthYear, num, personName, splitCandidate } from "./listsModel.mjs";
import { isTyping, useLists } from "./parts";
import "./lists.css";

/*
 * CHECK THESE PEOPLE (frame 7): everyone put together from records at
 * different companies only because the names matched. Records joined by
 * LinkedIn, by email or by hand are not asked about.
 *
 * Y = same person (every record locked as linked by you, so it never comes
 * back), S = split the questioned record out, J = skip for now. The one in
 * focus is the first one not yet answered.
 */
export default function CheckPeoplePage() {
  const [items, setItems] = useState(null);
  const [done, setDone] = useState({}); // person_id -> "same" | "split" | "skip"
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [peek, setPeek] = useState(null);
  const [{ lists }] = useLists();

  const load = useCallback(async () => {
    try {
      setItems((await api("/people/review")).people);
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const open = (items || []).filter((i) => !done[i.person_id]);
  const focus = open[0] || null;
  const checkedNow = Object.values(done).filter((v) => v !== "skip").length;

  const answer = useCallback(
    async (item, verdict) => {
      if (!item || busy) return;
      setBusy(true);
      setErr(null);
      try {
        if (verdict === "same") await api(`/people/${item.person_id}/confirm`, { method: "POST" });
        if (verdict === "split") {
          const c = splitCandidate(item);
          if (c) await api(`/people/${item.person_id}/split`, { method: "POST", body: JSON.stringify({ contact_id: c.id }) });
        }
        setDone((d) => ({ ...d, [item.person_id]: verdict }));
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusy(false);
      }
    },
    [busy]
  );

  useEffect(() => {
    const key = (e) => {
      if (peek || !focus || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e)) return;
      const k = e.key.toLowerCase();
      if (k === "y") answer(focus, "same");
      else if (k === "s") answer(focus, "split");
      else if (k === "j") answer(focus, "skip");
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [focus, answer, peek]);

  return (
    <>
      <div style={{ padding: "14px 24px 0" }}><Link className="lst-crumb" to="/lists">Lists /</Link></div>
      <Topbar
        title="Check these people"
        sub={items ? <><b style={{ color: "var(--crm-black)" }}>{num(open.length)} left</b>{checkedNow ? ` · ${num(checkedNow)} checked now` : ""}</> : ""}
      >
        <span className="lst-sub">
          <span className="lst-kbd" style={{ color: "var(--crm-black)" }}>Y</span> same person · <span className="lst-kbd" style={{ color: "var(--crm-black)" }}>S</span> split · <span className="lst-kbd" style={{ color: "var(--crm-black)" }}>J</span> skip
        </span>
      </Topbar>
      <div className="lst-page">
        <div className="lst-sub">
          Each of these people was put together from records at different companies only because the names matched — no shared LinkedIn, no shared email. Records joined by LinkedIn or by you are not asked about.
        </div>
        {err && <p className="crm-login__err">{err}</p>}
        {items && !items.length && <div className="lst-sub">Nothing to check.</div>}
        {(items || []).map((item) => {
          const verdict = done[item.person_id];
          if (verdict === "skip") return null;
          const recs = [...(item.contacts || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          const cand = splitCandidate(item);
          const current = new Set([item.email_contact_id, item.role_contact_id]);
          return (
            <article key={item.person_id} className={`lst-item${focus === item ? " is-focus" : ""}${verdict ? " is-done" : ""}`}>
              <div className="lst-item__head">
                <span className="lst-item__name">{personName(item)}</span>
                {verdict ? (
                  <span className="lst-pill">{verdict === "same" ? "Same person · by you" : `Split ${cand?.company || "a record"} out · by you`}</span>
                ) : (
                  <span className="lst-sub">{recs.length} records{item.lists?.length ? ` · on ${item.lists.join(", ")}` : ""}</span>
                )}
              </div>
              <div className="lst-recs">
                {recs.map((c, i) => (
                  <React.Fragment key={c.id}>
                    {i > 0 && (
                      <div className="lst-linker">
                        <span className="lst-join">{joinWord(c.link) || "joined"}</span>
                        <span className={`lst-linker__ln${c.link === "name" ? " lst-linker__ln--q" : ""}`} />
                        {c.link === "name" && focus === item && <span>asking about this</span>}
                      </div>
                    )}
                    <div className={`lst-rec${current.has(c.id) ? " is-cur" : ""}`}>
                      <span className="lst-sub">{monthYear(c.created_at)}{current.has(c.id) ? " · current" : ""}</span>
                      <b style={c.company ? null : { color: "var(--crm-gray-600)" }}>{c.company || "No company"}</b>
                      {c.job_title && <span className="lst-rec__t">{c.job_title}</span>}
                      <span className="lst-rec__e">{c.email || "no email"}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {!verdict && (
                <div className="lst-item__acts">
                  <button className="crm-btn crm-btn--secondary" type="button" disabled={busy} onClick={() => answer(item, "same")}>
                    Same person ✓ {focus === item && <span className="lst-kbd">Y</span>}
                  </button>
                  <button className="crm-btn crm-btn--secondary" type="button" disabled={busy || !cand} onClick={() => answer(item, "split")}>
                    Not the same — split {cand?.company || "it"} out {focus === item && <span className="lst-kbd">S</span>}
                  </button>
                  {focus === item && (
                    <button className="crm-btn crm-btn--ghost" type="button" disabled={busy} onClick={() => answer(item, "skip")}>Skip <span className="lst-kbd">J</span></button>
                  )}
                  <span style={{ flexGrow: 1 }} />
                  <button type="button" className="lst-link" onClick={() => setPeek(item.person_id)}>Open person</button>
                </div>
              )}
            </article>
          );
        })}
      </div>
      {peek && (
        <aside className="crm-peek crm-leads__peek">
          <PersonPanel personId={peek} allLists={lists} onClose={() => setPeek(null)} onChange={load} />
        </aside>
      )}
    </>
  );
}
